/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { Ring } from '@uiball/loaders';
import DataTable from '../../../../shared/components/DataTable';
import TiersContainer from '../../auction/components/TierCard';
import StatusBadge from '../../auction/components/StatusBadge';
import useCountdown from '../../auction/components/CountdownTimer';
import { useChainContext } from '../../../../containers/context/context';
import { calculateCountdown } from '../../../../utils/dates';
import {
  PageContainer,
  BackButton,
  AuctionTitle,
  AuctionMeta,
  PrimaryButton,
  LoadingOverlay,
} from '../../auction/components/shared';

const JoinAuctionButton = styled(PrimaryButton)`
  margin-top: 1.5rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  background: #0B1122;
  border-radius: 0.75rem;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  color: white;
`;

const TierOption = styled.label`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(92, 233, 255, 0.5);
  }
  
  ${props => props.isHighlighted && `
    border: 3px solid #5ce9ff;
    box-shadow: 0 0 15px rgba(92, 233, 255, 0.3);
  `}
  
  input[type="radio"] {
    margin-right: 1rem;
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #5ce9ff;
  }
  
  input[type="radio"]:checked + div {
    border-color: #5ce9ff;
  }
`;

const TierOptionContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${props => props.isHighlighted && `
    padding-right: 1.5rem;
  `}
`;

const TierOptionLabel = styled.span`
  color: white;
  font-size: 1rem;
  font-weight: 500;
`;

const TierOptionPrice = styled.span`
  color: #5ce9ff;
  font-size: 1rem;
  font-weight: 500;
`;

const TierOptionBidCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
  color: #5ce9ff;
  font-size: 0.875rem;
`;

const BidIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  flex-shrink: 0;
`;

const ModalPulsingDot = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #5ce9ff;
  box-shadow: 0 0 0 0 rgba(92, 233, 255, 1);
  animation: pulse 2s infinite;
  z-index: 1;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(92, 233, 255, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(92, 233, 255, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(92, 233, 255, 0);
    }
  }
`;

const BidsTableSection = styled.div`
  margin-top: 2.5rem;
`;

const BidsTableTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin: 0 0 1.5rem 0;
`;

const EmptyBidsMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
`;

const UserIdCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`;

const UserAvatarPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.1);
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.span`
  font-size: 0.75rem;
  color: #5ce9ff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserIdText = styled.span`
  font-family: monospace;
  font-size: 0.75rem;
  color: #9ca3af;
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: #5ce9ff;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #00f7d2;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const CopyIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
`;

const FilterToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  user-select: none;
`;

const ToggleSwitch = styled.input`
  width: 44px;
  height: 24px;
  appearance: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  
  &:checked {
    background: #5ce9ff;
  }
  
  &:before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: 2px;
    transition: transform 0.2s;
  }
  
  &:checked:before {
    transform: translateX(20px);
  }
`;

const AddBidButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  color: black;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 1rem;
  transition: opacity 0.2s;
  margin-top: 1.5rem;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: white;
  }
`;

const DeliveryAddressLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  color: #9ca3af;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
`;

const DeliveryAddressInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.5rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1rem;
  margin-top: 1rem;
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const AuctionDetailView = () => {
  const history = useHistory();
  const { auctionId, storeId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [auctionData, setAuctionData] = useState(null);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  const [endsIn, setEndsIn] = useState('Ongoing');
  const [isAuctionEnded, setIsAuctionEnded] = useState(false);
  const [showMyBidsOnly, setShowMyBidsOnly] = useState(false);
  const [startsIn, setStartsIn] = useState('');
  const [isAuctionStarted, setIsAuctionStarted] = useState(true);
  const [highlightedTierId, setHighlightedTierId] = useState(null);
  const [aggregateQuantity, setAggregateQuantity] = useState(null);

  const user = JSON.parse(localStorage.getItem('user'));
  const currentUserId = user?.data?.user?.user_id || user?.data?.user?._id || user?.data?.user?.id;
  const userRole = user?.data?.user?.role || '';
  const senderWallet = user?.data?.user?.address || '';

  const isAuctionOwner = auctionData?.user_id === currentUserId;

  const { socket } = useChainContext();

  useEffect(() => {
    if (!socket) return;
    const handleBidResponse = (res) => {
      setAuctionData(res.data[0]);
    };
  
      setIsLoading(true);
      socket.emit("auction-detail", {
        auction_id: auctionId,
      });

      socket.on("add-auction-bid-response", handleBidResponse);

      setIsLoading(false);
 
  
  }, [socket, auctionId]);
  

  const listenerReadyRef = useRef(false);

  const emitAuctionDetail = useCallback(() => {
    if (socket && socket.connected && listenerReadyRef.current && auctionId) {
      setIsLoading(true);
      socket.emit('auction-detail', {
        auction_id: auctionId
      });
      socket.on('add-auction-bid-response', (res)=>{
      });

    }
  }, [socket, auctionId]);
  
  const handleAddAuctionBidResponse = useCallback((response) => {
    setIsLoading(false);

    const isBidSubmission = response?.message === 'Successfully submitted' || response?.success;

    if (isBidSubmission) {
      setIsSubmittingBid(false);

      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const auctionDataFromSocket = response.data.find(auction => auction._id === auctionId) || response.data[0];
        setAuctionData(auctionDataFromSocket);
      }
      setShowBidModal(false);
      setSelectedTierId(null);
      setDeliveryAddress('');

      if (socket && socket.connected) {
        socket.emit('fetch_auction_detail', {
          auction_id: auctionId
        });
      }
    } else if (response?.data) {
      if (Array.isArray(response.data) && response.data.length > 0) {
        const auctionDataFromSocket = response.data.find(auction => auction._id === auctionId) || response.data[0];
        setAuctionData(auctionDataFromSocket);
      } else if (typeof response.data === 'object' && response.data !== null) {
        setAuctionData(response.data);
      } else {
        toast.error('Auction not found');
        history.push(storeId ? `/storedetail/${storeId}` : '/auction');
      }
    } else if (response && typeof response === 'object' && response._id) {
      setAuctionData(response);
    } else {
      setIsSubmittingBid(false);
      const errorMessage = response?.Message || response?.message || response?.error;
      if (errorMessage && errorMessage !== 'Failed to add bid') {
        toast.error(errorMessage, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    }
  }, [auctionId, socket, storeId, history]);

  // Set up socket event listeners
  useEffect(() => {
    if (!socket) {
      listenerReadyRef.current = false;
      return;
    }

    // Listen for bid response
    socket.on('add-auction-bid-response', handleAddAuctionBidResponse);
    listenerReadyRef.current = true;

    // Listen for socket connection event
    const handleConnect = () => {
      listenerReadyRef.current = true;
      if (auctionId && socket.connected) {
        setTimeout(() => {
          emitAuctionDetail();
        }, 500);
      }
    };

    socket.on('connect', handleConnect);

    // If already connected, emit auction detail request
    if (socket.connected && auctionId) {
      setTimeout(() => {
        emitAuctionDetail();
      }, 500);
    }

    // Cleanup listeners on unmount or when dependencies change
    return () => {
      if (socket) {
        socket.off('add-auction-bid-response', handleAddAuctionBidResponse);
        socket.off('connect', handleConnect);
      }
      listenerReadyRef.current = false;
    };
  }, [socket, handleAddAuctionBidResponse, emitAuctionDetail, auctionId]);

  useEffect(() => {
    if (!auctionId) {
      setIsLoading(false);
      return;
    }

    if (!socket) {
      return;
    }

    let timeout;
    timeout = setTimeout(() => {
      setIsLoading(false);
    }, 10000);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [auctionId, socket]);

  // Set up listener for auction detail updates
  useEffect(() => {
    if (!auctionId || !socket) {
      return;
    }

    const handleReceivedAuctionDetail = (response) => {
      if (!response) {
        return;
      }

      if (response?.data) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          const auctionDataFromSocket = response.data.find(auction => auction._id === auctionId) || response.data[0];
          if (auctionDataFromSocket && auctionDataFromSocket._id) {
            setIsLoading(false);
            setAuctionData(auctionDataFromSocket);
            return;
          }
        } else if (typeof response.data === 'object' && response.data !== null) {
          if (response.data._id) {
            setIsLoading(false);
            setAuctionData(response.data);
            return;
          } else if (response.data.tier_id) {
            setHighlightedTierId(response.data.tier_id);
            if (response.data.quantity !== undefined) {
              setAggregateQuantity(response.data.quantity);
            }
            return;
          }
        }
      }

      if (response && typeof response === 'object' && response._id && !response.tier_id) {
        setIsLoading(false);
        setAuctionData(response);
        return;
      }

      if (response?.tier_id) {
        setHighlightedTierId(response.tier_id);
        if (response?.quantity !== undefined) {
          setAggregateQuantity(response.quantity);
        }
      } else {
        handleAddAuctionBidResponse(response);
      }
    };

    // Listen for auction detail updates
    socket.on('received_auction_detail', handleReceivedAuctionDetail);

    // Request auction detail if socket is connected
    if (socket.connected) {
      socket.emit('fetch_auction_detail', {
        auction_id: auctionId
      });
    }

    // Cleanup listener on unmount or when dependencies change
    return () => {
      if (socket) {
        socket.off('received_auction_detail', handleReceivedAuctionDetail);
      }
    };
  }, [auctionId, socket, handleAddAuctionBidResponse]);


  useEffect(() => {
    if (!auctionData) {
      setHighlightedTierId(null);
      return;
    }

    const tiers = auctionData?.auction_tier || auctionData?.tiers || [];
    console.log("🚀 ~ AuctionDetailView ~ tiers:", tiers)
    const biddingDetails = auctionData?.bidding_details || [];

    if (!tiers || tiers.length === 0) {
      setHighlightedTierId(null);
      return;
    }

    const getBidCount = (tierId) => {
      if (!tierId || !biddingDetails || biddingDetails.length === 0) return 0;
      return biddingDetails.filter(bid => bid.tier_id === tierId).length;
    };

    const totalBidQuantity = biddingDetails.reduce((sum, bid) => {
      const quantity = bid.quantity || bid.qty || bid.amount || bid.total_quantity || 0;
      return sum + (Number(quantity) || 0);
    }, 0);

    let matchingTier = null;

    if (totalBidQuantity > 0) {
      matchingTier = tiers.find((tier) => {
        const tierQty = tier.minQty || tier.qty || tier.quantity || 0;
        return Number(tierQty) === totalBidQuantity;
      });

      if (!matchingTier && tiers.length > 0) {
        let closestTier = tiers[0];
        let minDiff = Math.abs((tiers[0].minQty || tiers[0].qty || tiers[0].quantity || 0) - totalBidQuantity);

        tiers.forEach((tier) => {
          const tierQty = tier.minQty || tier.qty || tier.quantity || 0;
          const diff = Math.abs(Number(tierQty) - totalBidQuantity);
          if (diff < minDiff) {
            minDiff = diff;
            closestTier = tier;
          }
        });

        matchingTier = closestTier;
      }
    }

    if (matchingTier) {
      setHighlightedTierId(matchingTier._id);
    } else {
      setHighlightedTierId(null);
    }
  }, [auctionData]);

  useEffect(() => {
    if (!auctionData) return;

    const endTime = auctionData.acution_end_time || auctionData.auction_end_time || auctionData.endDate;
    if (!endTime) {
      setEndsIn('Ongoing');
      return;
    }

    const updateCountdown = () => {
      const { countdown, isEnded } = calculateCountdown(endTime);
      setEndsIn(countdown);
      setIsAuctionEnded(isEnded);
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [auctionData]);

  useEffect(() => {
    if (!auctionData) return;

    const startTime = auctionData.auction_start_time || auctionData.startDate;
    if (!startTime) {
      setIsAuctionStarted(true);
      setStartsIn('');
      return;
    }

    const startDate = new Date(startTime);

    const updateStartCountdown = () => {
      const nowUTC = Date.now();
      const diff = startDate.getTime() - nowUTC;

      if (diff <= 0) {
        setIsAuctionStarted(true);
        setStartsIn('');
        return;
      }

      setIsAuctionStarted(false);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setStartsIn(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else if (hours > 0) {
        setStartsIn(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setStartsIn(`${minutes}m ${seconds}s`);
      } else {
        setStartsIn(`${seconds}s`);
      }
    };

    updateStartCountdown();

    const interval = setInterval(updateStartCountdown, 1000);

    return () => clearInterval(interval);
  }, [auctionData]);


  const handleJoinAuction = () => {
    const tiers = auctionData?.auction_tier || auctionData?.tiers || [];
    if (tiers.length === 0) {
      toast.error('No tiers available for this auction', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    setShowBidModal(true);
    setSelectedTierId(null);
  };

  const handleCloseBidModal = () => {
    setShowBidModal(false);
    setSelectedTierId(null);
    setDeliveryAddress('');
  };

  const handleAddBid = () => {
    if (!selectedTierId) {
      toast.error('Please select a tier', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!deliveryAddress || deliveryAddress.trim() === '') {
      toast.error('Please enter a delivery address', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!socket || !socket.connected) {
      toast.error('Socket not connected. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setIsSubmittingBid(true);
    const socketPayload = {
      user_id: currentUserId,
      senderWallet: senderWallet,
      tier_id: selectedTierId,
      auction_id: auctionId,
      delivery_address: deliveryAddress,
    };

    socket.emit('add-auction-bid', socketPayload);

  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingOverlay>
          <Ring size={65} color="#00f7d2" />
        </LoadingOverlay>
      </PageContainer>
    );
  }

  if (!auctionData) {
    return (
      <PageContainer>
        <BackButton onClick={() => history.push(storeId ? `/storedetail/${storeId}` : '/auction')}>
          ← Back
        </BackButton>
        <AuctionTitle>Auction Not Found</AuctionTitle>
      </PageContainer>
    );
  }

  const tiers = auctionData?.auction_tier || auctionData?.tiers || [];

  const biddingDetails = auctionData?.bidding_details || [];

  const getBidCount = (tierId) => {
    if (!tierId || !biddingDetails || biddingDetails.length === 0) return 0;
    return biddingDetails.filter(bid => bid.tier_id === tierId).length;
  };

  const formatUserId = (userId) => {
    if (!userId || userId.length <= 5) return userId;
    const first = userId.substring(0, 3);
    const last = userId.substring(userId.length - 2);
    return `${first}...${last}`;
  };

  const copyUserId = async (userId) => {
    try {
      await navigator.clipboard.writeText(userId);
      toast.success('User email copied to clipboard', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      toast.error('Failed to copy user email', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const filteredBiddingDetails = showMyBidsOnly && currentUserId
    ? biddingDetails.filter(bid => bid.user_id === currentUserId)
    : biddingDetails;

  return (
    <PageContainer>
      <BackButton onClick={() => history.push(storeId ? `/storedetail/${storeId}` : '/auction')}>
        ← {storeId ? 'Back to Store' : 'Back to Auctions'}
      </BackButton>

      <AuctionTitle>{auctionData.title || 'Untitled Auction'}</AuctionTitle>
      <AuctionMeta>
        {!isAuctionStarted
          ? `Starts in ${startsIn}`
          : isAuctionEnded
            ? 'Ended'
            : `Ends in ${endsIn}`
        }
      </AuctionMeta>

      {tiers.length > 0 ? (
        <TiersContainer>
          {tiers.map((tier, index) => {
            const bidCount = getBidCount(tier._id);
            const isHighlighted = highlightedTierId === tier._id;
            return (
              <TiersContainer.Tier
                key={tier._id || index}
                tier={tier}
                symbol={auctionData?.symbol}
                bidCount={bidCount}
                showBidCount={true}
                isHighlighted={isHighlighted}
                aggregateQuantity={isHighlighted ? aggregateQuantity : null}
              />
            );
          })}
        </TiersContainer>
      ) : (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
          No pricing tiers available
        </div>
      )}

      {!isAuctionOwner && (
        <JoinAuctionButton
          onClick={handleJoinAuction}
          disabled={!isAuctionStarted || isAuctionEnded}
        >
          {!isAuctionStarted
            ? 'Auction Not Started'
            : isAuctionEnded
              ? 'Auction Ended'
              : 'Join Auction'
          }
        </JoinAuctionButton>
      )}
      {isAuctionOwner && (
        <div style={{
          padding: '0.75rem 1.5rem',
          marginTop: '1.5rem',
          borderRadius: '0.75rem',
          background: 'rgba(255, 255, 255, 0.1)',
          color: '#9ca3af',
          textAlign: 'center',
          fontSize: '0.875rem'
        }}>
          You cannot bid on your own auction
        </div>
      )}

      <BidsTableSection>
        <BidsTableTitle>Bid Information</BidsTableTitle>
        <FilterToggle>
          <FilterLabel>
            <ToggleSwitch
              type="checkbox"
              checked={showMyBidsOnly}
              onChange={(e) => setShowMyBidsOnly(e.target.checked)}
            />
            <span>Show My Bids Only</span>
          </FilterLabel>
        </FilterToggle>
        <DataTable
          columns={[
            {
              key: 'user_id',
              label: 'User',
              render: (value, row) => {
                if (!value) return 'N/A';

                const userInfo = row.user_info;
                let userName = '';
                let userImage = '';
                let userEmail = '';

                if (userInfo) {
                  if (Array.isArray(userInfo) && userInfo.length > 0) {
                    userName = userInfo[0].name || '';
                    userImage = userInfo[0].image || '';
                    userEmail = userInfo[0].email || '';
                  } else if (typeof userInfo === 'object') {
                    userName = userInfo.name || '';
                    userImage = userInfo.image || '';
                    userEmail = userInfo.email || '';
                  }
                }

                return (
                  <UserIdCell>
                    {userImage ? (
                      <UserAvatar src={userImage} alt={userName || 'User'} />
                    ) : (
                      <UserAvatarPlaceholder>
                        {userName ? userName.charAt(0).toUpperCase() : 'U'}
                      </UserAvatarPlaceholder>
                    )}
                    <UserInfo>
                      <UserName>{userName || formatUserId(value)}</UserName>
                      {userEmail && <UserEmail>{userEmail}</UserEmail>}
                    </UserInfo>
                    <CopyButton
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUserId(userEmail);
                      }}
                      title="Copy User email"
                    >
                      <CopyIcon viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </CopyIcon>
                    </CopyButton>
                  </UserIdCell>
                );
              }
            },
            {
              key: 'date',
              label: 'Date',
              render: (value, row) => {
                return row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A';
              }
            },
            {
              key: 'tier',
              label: 'Tier',
              render: (value, row) => {
                const tier = tiers.find(t => t._id === row.tier_id);
                return tier ? `${tier.minQty || tier.qty} units` : 'N/A';
              }
            },
            {
              key: 'symbol',
              label: 'Symbol'
            },
            {
              key: 'price',
              label: 'Price',
              render: (value, row) => {
                return row.actual_price !== undefined ? `${row.actual_price} / unit` : 'N/A';
              }
            },
            {
              key: 'delivery_address',
              label: 'Delivery Address'
            },
            {
              key: 'status',
              label: 'Status',
              render: (value, row) => (
                <StatusBadge status={value || 'pending'}>
                  {value || 'pending'}
                </StatusBadge>
              )
            }
          ]}
          data={filteredBiddingDetails}
          emptyMessage={
            showMyBidsOnly
              ? 'You have not placed any bids for this auction yet.'
              : 'No bids have been placed yet for this auction.'
          }
          keyExtractor={(item) => item._id}
        />
      </BidsTableSection>

      {showBidModal && (
        <ModalOverlay onClick={handleCloseBidModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseBidModal}>×</CloseButton>
            <ModalTitle>Select Tier</ModalTitle>

            {tiers.length > 0 ? (
              <>
                {tiers.map((tier, index) => {
                  const isHighlighted = highlightedTierId === tier._id;
                  return (
                    <TierOption key={tier._id || index} isHighlighted={isHighlighted}>
                      {isHighlighted && <ModalPulsingDot />}
                      <input
                        type="radio"
                        name="tier"
                        value={tier._id}
                        checked={selectedTierId === tier._id}
                        onChange={(e) => setSelectedTierId(e.target.value)}
                      />
                      <TierOptionContent isHighlighted={isHighlighted}>
                        <TierOptionLabel>
                          {tier.minQty || tier.qty} units
                        </TierOptionLabel>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <TierOptionPrice>
                            {auctionData?.symbol ? `${auctionData.symbol} ` : ''}{tier.price} / unit
                          </TierOptionPrice>
                          {(() => {
                            const bidCount = getBidCount(tier._id);
                            return bidCount > 0 ? (
                              <TierOptionBidCount>
                                <BidIcon viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </BidIcon>
                                <span>{bidCount} {bidCount === 1 ? 'bid' : 'bids'}</span>
                              </TierOptionBidCount>
                            ) : null;
                          })()}
                        </div>
                      </TierOptionContent>
                    </TierOption>
                  );
                })}

                <DeliveryAddressLabel>
                  Delivery Address *
                </DeliveryAddressLabel>
                <DeliveryAddressInput
                  type="text"
                  placeholder="Enter delivery address"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                />

                <AddBidButton
                  onClick={handleAddBid}
                  disabled={!selectedTierId || !deliveryAddress.trim() || isSubmittingBid}
                >
                  {isSubmittingBid ? 'Adding Bid...' : 'Add Bid'}
                </AddBidButton>
              </>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                No tiers available
              </div>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default AuctionDetailView;

